import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { Clock, Users, Laptop, Smartphone, Award, Calendar as CalIcon, ChevronLeft, Ticket, Check, Infinity as InfIcon, CalendarDays, BookOpen } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import CourseChat from "../components/CourseChat";
import LeadCapture from "../components/LeadCapture";
import { Analytics } from "../lib/analytics";

export default function CourseDetail() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [slots, setSlots] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [voucherMsg, setVoucherMsg] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [plan, setPlan] = useState("full");
  const [enrolled, setEnrolled] = useState(false);
  const [freeSuccess, setFreeSuccess] = useState(false);
  const [syllabusOpen, setSyllabusOpen] = useState(false);
  const [mobileStickyVisible, setMobileStickyVisible] = useState(false);
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianConsent, setGuardianConsent] = useState(false);
  const { user } = useAuth();
  const { t, lang, currency, formatPrice } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get("canceled");
  const isKidsCourse = !!course?.category?.startsWith("kids-");

  // Pre-fill guardian fields from the logged-in account — the registering
  // account is expected to already be the parent/guardian's own.
  useEffect(() => {
    if (user) {
      setGuardianName((n) => n || user.name || "");
      setGuardianEmail((e) => e || user.email || "");
    }
  }, [user]);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data));
    api.get(`/courses/${id}/slots`).then((r) => setSlots(r.data || []));
  }, [id]);

  // Inject Course JSON-LD structured data for SEO
  useEffect(() => {
    if (!course) return;
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": course.title,
      "description": course.summary || course.title,
      "url": `${siteUrl}/courses/${id}`,
      "provider": {
        "@type": "EducationalOrganization",
        "name": "EDUHUB",
        "url": siteUrl,
      },
      "educationalLevel": course.level,
      "teaches": (course.tags || []).join(", "),
      "offers": {
        "@type": "Offer",
        "price": course.price_usd,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
      },
    };
    if (course.teacher) {
      schema["instructor"] = { "@type": "Person", "name": course.teacher.name };
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = `course-schema-${id}`;
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(`course-schema-${id}`);
      if (el) el.remove();
    };
  }, [course, id]);

  // Per-page title + BreadcrumbList schema
  useEffect(() => {
    if (!course) return;
    document.title = `${course.title} | EDUHUB Academy`;
    const siteUrl = process.env.REACT_APP_SITE_URL || "https://eduhubcenter.com";
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
        { "@type": "ListItem", "position": 2, "name": "Courses", "item": `${siteUrl}/courses` },
        { "@type": "ListItem", "position": 3, "name": course.title, "item": `${siteUrl}/courses/${id}` },
      ],
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = `breadcrumb-schema-${id}`;
    el.text = JSON.stringify(breadcrumb);
    document.head.appendChild(el);
    return () => {
      document.title = "EDUHUB — A Classical Academy for All Ages";
      const s = document.getElementById(`breadcrumb-schema-${id}`);
      if (s) s.remove();
    };
  }, [course, id]);

  // Show mobile sticky CTA after scrolling past 400px (sidebar invisible on mobile)
  useEffect(() => {
    const onScroll = () => setMobileStickyVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Check if current user is enrolled (controls chat + slot picker visibility)
  useEffect(() => {
    if (!user || user.role !== "student") {
      setEnrolled(false);
      return;
    }
    api
      .get("/student/enrollments")
      .then((r) => {
        const has = (r.data || []).some((e) => e.course_id === id);
        setEnrolled(has);
      })
      .catch(() => setEnrolled(false));
  }, [id, user]);

  const enroll = async () => {
    setErr("");
    setFreeSuccess(false);
    Analytics.enrollClick(id, course?.title, course?.price_usd);
    if (!user || user.role !== "student") {
      navigate("/login", { state: { from: { pathname: `/courses/${id}` } } });
      return;
    }
    if (isKidsCourse && (!guardianConsent || !guardianName.trim() || !guardianEmail.trim())) {
      setErr("Please confirm you are the parent/guardian and fill in your name and email to enroll a child.");
      return;
    }
    try {
      setBusy(true);
      const { data } = await api.post("/enroll/checkout", {
        course_id: id,
        currency: currency.toLowerCase(),
        voucher_code: voucherCode.trim() || undefined,
        plan,
        ...(isKidsCourse ? {
          guardian_name: guardianName.trim(),
          guardian_email: guardianEmail.trim(),
          guardian_consent: guardianConsent,
        } : {}),
      });
      if (data.free) {
        // Free enrollment confirmed — no Stripe session, access granted immediately.
        setFreeSuccess(true);
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const applyVoucher = async () => {
    setVoucherMsg("");
    setVoucherInfo(null);
    const code = voucherCode.trim();
    if (!code) return;
    if (!user) {
      setVoucherMsg("Sign in to apply a code.");
      return;
    }
    try {
      const { data } = await api.post("/auth/check-voucher", { code, course_id: id });
      if (data.valid) {
        setVoucherInfo(data);
        setVoucherMsg("");
      } else {
        setVoucherMsg(data.reason || "Invalid code");
      }
    } catch (e) {
      setVoucherMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!course) {
    return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 text-eduhu-inkSoft">Loading…</div>;
  }
  const title = (lang === "ro" && course.title_ro) || course.title;
  const progress = Math.min(100, Math.round(((course.enrolled_count || 0) / Math.max(1, course.min_students)) * 100));

  // Group slots by date for the calendar widget
  const slotsByDay = {};
  slots.forEach((s) => {
    const d = new Date(s.start_at).toDateString();
    if (!slotsByDay[d]) slotsByDay[d] = [];
    slotsByDay[d].push(s);
  });
  const slotDates = Object.keys(slotsByDay).map((d) => new Date(d));
  const filteredSlots = selectedDate
    ? slotsByDay[selectedDate.toDateString()] || []
    : slots;

  return (
    <div data-testid="course-detail-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-10">
        <Link to="/courses" className="inline-flex items-center gap-1.5 text-sm text-eduhu-inkSoft nav-link" data-testid="back-to-courses">
          <ChevronLeft size={14} /> All courses
        </Link>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="overline text-eduhu-claret">{course.category}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-eduhu-dark mt-3">{title}</h1>
          <p className="mt-5 text-lg text-eduhu-inkSoft leading-relaxed">{course.summary}</p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Stat
              icon={course.continuous ? <InfIcon size={18} /> : <CalendarDays size={18} />}
              label="Schedule"
              value={course.continuous ? "Continuous" : `${course.weeks || 6} weeks`}
            />
            <Stat icon={<Clock size={18} />} label="Sessions / week" value={course.sessions_per_week || 3} />
            <Stat icon={<Users size={18} />} label="Min cohort" value={course.min_students} />
            <Stat
              icon={course.requires_laptop ? <Laptop size={18} /> : <Smartphone size={18} />}
              label="Device"
              value={course.requires_laptop ? "Laptop required" : "Phone or laptop"}
            />
          </div>

          {course.teacher && (
            <div className="mt-12 p-7 border border-eduhu-border bg-eduhu-bg2" data-testid="course-teacher">
              <div className="overline text-eduhu-inkSoft mb-2">Teacher</div>
              <div className="font-serif text-2xl text-eduhu-dark">{course.teacher.name}</div>
              <div className="text-eduhu-inkSoft mt-2 text-sm leading-relaxed">{course.teacher.bio}</div>
            </div>
          )}

          <div className="mt-12">
            <h3 className="font-serif text-2xl text-eduhu-dark">What you will master</h3>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3 text-eduhu-inkSoft">
              {(course.tags || []).map((t) => (
                <li key={t} className="border border-eduhu-border bg-eduhu-bg2 px-4 py-3 text-sm capitalize">
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {slots.length > 0 && enrolled && (
            <div className="mt-12" data-testid="available-slots">
              <h3 className="font-serif text-2xl text-eduhu-dark mb-4">Pick a date & book a session</h3>
              <div className="grid md:grid-cols-12 gap-6">
                <div className="md:col-span-5 border border-eduhu-border bg-eduhu-bg2 p-3">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    modifiers={{ available: slotDates }}
                    modifiersClassNames={{
                      available: "text-eduhu-gold font-semibold underline underline-offset-4",
                    }}
                    className="mx-auto"
                  />
                  <div className="text-xs text-eduhu-inkSoft px-3 pb-2 flex items-center justify-between">
                    <span className="text-eduhu-gold">●</span> dates with available slots
                    {selectedDate && (
                      <button
                        onClick={() => setSelectedDate(null)}
                        className="nav-link text-eduhu-claret"
                      >
                        clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="md:col-span-7 space-y-3">
                  {filteredSlots.length === 0 ? (
                    <div className="p-5 border border-dashed border-eduhu-border text-sm text-eduhu-inkSoft">
                      No open slots {selectedDate ? "on this date" : "yet"}.
                    </div>
                  ) : (
                    filteredSlots.map((s) => (
                      <SlotRow
                        key={s.id}
                        slot={s}
                        courseId={id}
                        onBooked={() =>
                          api.get(`/courses/${id}/slots`).then((r) => setSlots(r.data || []))
                        }
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
          {(enrolled || user?.role === "teacher" || user?.role === "admin") && (
            <CourseChat courseId={id} courseTitle={title} />
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-28 eduhu-card p-7" data-testid="enroll-panel">
            {/* Plan toggle for continuous courses */}
            {course.continuous && course.subscription_price_monthly_usd > 0 && (
              <div className="mb-6 grid grid-cols-2 gap-px bg-eduhu-border border border-eduhu-border" data-testid="plan-toggle">
                <button
                  data-testid="plan-full"
                  onClick={() => setPlan("full")}
                  className={`px-3 py-3 text-xs uppercase tracking-wider transition-colors ${
                    plan === "full"
                      ? "bg-eduhu-dark text-eduhu-bg"
                      : "bg-eduhu-bg2 text-eduhu-ink hover:bg-eduhu-bg3"
                  }`}
                >
                  Pay in full
                </button>
                <button
                  data-testid="plan-monthly"
                  onClick={() => setPlan("monthly")}
                  className={`px-3 py-3 text-xs uppercase tracking-wider transition-colors ${
                    plan === "monthly"
                      ? "bg-eduhu-dark text-eduhu-bg"
                      : "bg-eduhu-bg2 text-eduhu-ink hover:bg-eduhu-bg3"
                  }`}
                >
                  Monthly
                </button>
              </div>
            )}
            <div className="overline text-eduhu-inkSoft">
              {plan === "monthly" ? "Per month" : "From"}
            </div>
            <div className="font-serif text-5xl text-eduhu-dark mt-1">
              {formatPrice(
                plan === "monthly"
                  ? Number(course.subscription_price_monthly_usd || 0)
                  : course.price_usd,
              )}
            </div>
            <div className="text-xs text-eduhu-inkSoft">
              {plan === "monthly"
                ? "billed monthly · cancel anytime"
                : course.continuous
                ? "annual access"
                : `${course.weeks || 6}-week cohort · ${course.sessions_per_week || 3}× live / week`}
            </div>

            <div className="mt-6">
              <div className="flex justify-between text-xs text-eduhu-inkSoft mb-1">
                <span>Cohort progress</span>
                <span>{course.enrolled_count || 0} / {course.min_students}</span>
              </div>
              <div className="h-1 bg-eduhu-bg3 overflow-hidden">
                <div className="h-full bg-eduhu-gold" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-2 text-xs text-eduhu-inkSoft">
                Class begins once the minimum cohort of {course.min_students} students is reached.
              </div>
            </div>

            {canceled && (
              <div className="mt-4 text-sm text-eduhu-error border-l-2 border-eduhu-error pl-3" data-testid="cancel-msg">
                Payment canceled. You can try again.
              </div>
            )}
            {err && (
              <div className="mt-4 text-sm text-eduhu-error border-l-2 border-eduhu-error pl-3" data-testid="enroll-error">
                {err}
              </div>
            )}
            {freeSuccess && (
              <div className="mt-4 text-sm text-eduhu-success border-l-2 border-eduhu-success pl-3" data-testid="free-enrollment-success">
                Enrollment completed successfully. Redirecting to your dashboard…
              </div>
            )}

            {/* Voucher input */}
            <div className="mt-6 pt-6 border-t border-eduhu-border" data-testid="voucher-block">
              <div className="overline text-eduhu-inkSoft mb-2 flex items-center gap-1.5">
                <Ticket size={12} /> Voucher or referral code
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="voucher-input"
                  value={voucherCode}
                  onChange={(e) => {
                    setVoucherCode(e.target.value.toUpperCase());
                    setVoucherInfo(null);
                    setVoucherMsg("");
                  }}
                  placeholder="LAUNCH10"
                  className="flex-1 bg-eduhu-bg border border-eduhu-border px-3 py-2 text-sm focus:outline-none focus:border-eduhu-dark"
                />
                <button
                  data-testid="voucher-apply"
                  onClick={applyVoucher}
                  className="eduhu-btn-ghost !py-2 !px-3 text-sm"
                  type="button"
                >
                  Apply
                </button>
              </div>
              {voucherInfo && (
                <div data-testid="voucher-ok" className="mt-2 text-xs text-eduhu-success flex items-center gap-1.5">
                  <Check size={12} />
                  {voucherInfo.kind === "percent"
                    ? `${voucherInfo.value}% off applied${voucherInfo.course_id ? " (this course only)" : ""}`
                    : voucherInfo.kind === "fixed"
                    ? `$${voucherInfo.value} off applied${voucherInfo.course_id ? " (this course only)" : ""}`
                    : `10% referral discount applied`}
                </div>
              )}
              {voucherMsg && (
                <div data-testid="voucher-err" className="mt-2 text-xs text-eduhu-error">
                  {voucherMsg}
                </div>
              )}
              <div className="mt-2 text-xs text-eduhu-inkSoft">
                Try <span className="font-mono text-eduhu-claret">LAUNCH10</span> for 10% off.
              </div>
            </div>

            {isKidsCourse && (
              <div className="mt-6 pt-6 border-t border-eduhu-border" data-testid="guardian-consent-block">
                <div className="overline text-eduhu-inkSoft mb-2">Parent / guardian details</div>
                <p className="text-xs text-eduhu-inkSoft mb-3">
                  This is a Kids Academy course. A parent or legal guardian must register the
                  account and confirm the details below — the child never manages their own
                  account or provides their own contact details.
                </p>
                <label className="block mb-2">
                  <div className="text-xs text-eduhu-inkSoft mb-1">Your name (parent/guardian)</div>
                  <input
                    data-testid="guardian-name-input"
                    value={guardianName}
                    onChange={(e) => setGuardianName(e.target.value)}
                    className="w-full bg-eduhu-bg border border-eduhu-border px-3 py-2 text-sm"
                  />
                </label>
                <label className="block mb-3">
                  <div className="text-xs text-eduhu-inkSoft mb-1">Your email (parent/guardian)</div>
                  <input
                    data-testid="guardian-email-input"
                    type="email"
                    value={guardianEmail}
                    onChange={(e) => setGuardianEmail(e.target.value)}
                    className="w-full bg-eduhu-bg border border-eduhu-border px-3 py-2 text-sm"
                  />
                </label>
                <label className="flex items-start gap-2 text-xs text-eduhu-ink cursor-pointer">
                  <input
                    data-testid="guardian-consent-checkbox"
                    type="checkbox"
                    checked={guardianConsent}
                    onChange={(e) => setGuardianConsent(e.target.checked)}
                    className="mt-0.5"
                  />
                  I confirm I am the parent or legal guardian of the child enrolling, and I
                  consent to their enrollment and participation in this course.
                </label>
              </div>
            )}

            <button
              data-testid="enroll-btn"
              disabled={busy || (isKidsCourse && !guardianConsent)}
              onClick={enroll}
              className="eduhu-btn-primary w-full justify-center mt-6"
            >
              {busy ? "Processing…" : t("course.enroll")}
            </button>
            {!user && (
              <div className="mt-3 text-xs text-eduhu-inkSoft text-center">
                You'll be asked to sign in or create an account.
              </div>
            )}
            <button
              onClick={() => { setSyllabusOpen(true); Analytics.syllabusRequest(course?.title); }}
              className="eduhu-btn-ghost w-full justify-center mt-3 text-sm flex items-center gap-2"
              data-testid="sidebar-syllabus-btn"
            >
              <BookOpen size={14} strokeWidth={1.5} /> Request syllabus
            </button>
            <div className="mt-6 pt-6 border-t border-eduhu-border text-xs text-eduhu-inkSoft space-y-2">
              <div className="flex items-center gap-2"><CalIcon size={13} /> Live, small-cohort sessions</div>
              <div className="flex items-center gap-2"><Award size={13} /> Verifiable completion certificate</div>
              <div className="flex items-center gap-2"><Users size={13} /> Capped seats — quality over scale</div>
            </div>
          </div>
        </aside>
      </div>

      {/* Mobile sticky enroll bar — hidden on lg where sidebar handles it */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-eduhu-bg border-t border-eduhu-border px-4 py-3 flex items-center gap-3 transition-all duration-300 ${
          mobileStickyVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
        }`}
        aria-hidden={!mobileStickyVisible}
      >
        <div className="flex-1 min-w-0">
          <div className="font-serif text-xl text-eduhu-dark">{course && formatPrice(course.price_usd)}</div>
          <div className="text-xs text-eduhu-inkSoft truncate">{course?.title}</div>
        </div>
        <button
          onClick={() => setSyllabusOpen(true)}
          className="eduhu-btn-ghost !py-2 !px-3 text-sm shrink-0"
        >
          <BookOpen size={14} aria-hidden="true" /> Syllabus
        </button>
        <button
          onClick={enroll}
          disabled={busy}
          className="eduhu-btn-primary !py-2.5 !px-4 text-sm shrink-0"
        >
          {busy ? "…" : t("course.enroll")}
        </button>
      </div>

      <LeadCapture
        variant="syllabus"
        open={syllabusOpen}
        onClose={() => setSyllabusOpen(false)}
        courseName={course?.title || ""}
      />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border-l border-eduhu-border pl-4">
      <div className="text-eduhu-claret">{icon}</div>
      <div className="mt-2 font-serif text-xl md:text-2xl text-eduhu-dark capitalize">{value}</div>
      <div className="overline text-eduhu-inkSoft">{label}</div>
    </div>
  );
}

function SlotRow({ slot, courseId, onBooked }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const start = new Date(slot.start_at);
  const end = new Date(slot.end_at);
  const book = async () => {
    setMsg("");
    setBusy(true);
    try {
      await api.post("/appointments/book", { slot_id: slot.id });
      setMsg("Booked.");
      onBooked && onBooked();
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-eduhu-border bg-eduhu-bg2 p-4 flex items-center justify-between gap-4">
      <div>
        <div className="font-serif text-lg text-eduhu-dark">{start.toLocaleString()}</div>
        <div className="text-xs text-eduhu-inkSoft">
          until {end.toLocaleTimeString()} · {slot.capacity - (slot.booked || 0)} seats left
        </div>
        {msg && <div className="text-xs text-eduhu-claret mt-1">{msg}</div>}
      </div>
      <button data-testid={`book-slot-${slot.id}`} disabled={busy} onClick={book} className="eduhu-btn-ghost !py-2 !px-3 text-sm">
        {busy ? "…" : "Book"}
      </button>
    </div>
  );
}
